import {
  Callout,
  DocPage,
  DocSection,
  K,
  MCP_URL,
  OL,
  P,
  PageFooter,
  PromptCard,
  UL,
} from "./_components";

export function Sharing() {
  return (
    <DocPage
      eyebrow="Collaborating"
      title="Sharing & teams"
      intro={
        <>
          Two ways to get other people onto your projects: a public link anyone
          can open with no signup, and a per-email invite for teammates who can
          read or edit. (To branch a project or copy a single widget, see{" "}
          <K>Remixing &amp; forking</K>.)
        </>
      }
    >
      <DocSection id="public" title="Public share link — for anyone, no signup">
        <P>
          The lowest-friction way to show off a project. On the project page,
          hit <K>Publish</K>. You get two URLs:
        </P>
        <UL>
          <li>
            <K>mcpbuild.app/p/&lt;token&gt;</K> — a website that renders every
            UI widget in the project. Pop it in a Slack message, embed it on a
            slide, link from a blog post.
          </li>
          <li>
            <K>{MCP_URL}/public/&lt;token&gt;</K> — a no-auth MCP endpoint.
            Anyone (or anything) can connect this in Claude / ChatGPT and call
            the project's tools. Mutating operations are blocked on the public
            endpoint, so people can't accidentally edit your project.
          </li>
        </UL>
        <PromptCard
          title="Publish a project"
          prompt={`Use project publish on my "Orders" project. Show me the public gallery URL and the public MCP URL.`}
        />
        <PromptCard
          title="Unpublish (revoke both URLs)"
          prompt={`Use project unpublish on my "Orders" project.`}
          note="Revokes both the gallery + the public MCP — both URLs immediately stop resolving."
        />
      </DocSection>

      <DocSection id="teams" title="Invite teammates by email">
        <P>
          For people you trust enough to edit. They sign in with the email you
          invited, see the project under <K>/shared</K>, and can be set to{" "}
          <K>read</K> or <K>write</K>:
        </P>
        <UL>
          <li>
            <K>read</K> — view widgets, view source, render widgets in their
            own Claude / ChatGPT via your project's MCP URL.
          </li>
          <li>
            <K>write</K> — everything above, plus edit widgets via{" "}
            <K>remix_widget</K> and toggle <K>exposed</K> on tools. Owner-only
            ops (publish, delete, secrets, footer) still require you.
          </li>
        </UL>
        <PromptCard
          title="Invite a teammate"
          prompt={`Use project share on my "Orders" project with email teammate@example.com and permission "write". Send them the invite.`}
          note={
            <>
              They get a "shared with you" email. Once they sign in, the
              project appears under <K>/shared</K> for them. To connect via MCP
              they use <K>mcpbuild.app/mcp/shared/&lt;shareId&gt;</K> (the
              invite email includes the URL).
            </>
          }
        />
        <PromptCard
          title="See who has access + whether they accepted"
          prompt={`Use project list_members on my "Orders" project.`}
          note={`Shows each member, their permission, and whether they've signed in yet — "active" or "invite pending".`}
        />
        <PromptCard
          title="Revoke a teammate's access"
          prompt={`Use project unshare on my "Orders" project for email teammate@example.com.`}
        />
        <Callout kind="note" title="Tool-level sharing">
          You can also share <em>one</em> tool from a project (handy when you
          want a teammate to use, say, a single dashboard widget without
          exposing the whole project). Pass <K>toolSlug</K> on the share/
          unshare call.
        </Callout>
      </DocSection>

      <DocSection id="when" title="Quick guide: which sharing mode?">
        <OL>
          <li>
            <strong>Random audience / demo</strong> → Publish. Send the{" "}
            <K>/p/&lt;token&gt;</K> gallery URL.
          </li>
          <li>
            <strong>Random audience who'll chat with the tool</strong> →
            Publish. Send the <K>/mcp/public/&lt;token&gt;</K> MCP URL.
          </li>
          <li>
            <strong>Specific teammate(s)</strong> → Invite by email. Pick{" "}
            <K>read</K> or <K>write</K>.
          </li>
          <li>
            <strong>Branch a project or copy one widget</strong> → see{" "}
            <K>Remixing &amp; forking</K>.
          </li>
        </OL>
      </DocSection>

      <PageFooter
        prev={{ to: "/docs/live-data", label: "Live data & secrets" }}
        next={{ to: "/docs/remixing", label: "Remixing & forking" }}
      />
    </DocPage>
  );
}
